/**
 * Two independent expiry clocks, and what the client is obliged to do about them.
 *
 * A session dies at whichever of these comes first:
 *
 *  - `idle_expires_at` — a **sliding** idle window (server default
 *    `SESSION_IDLE_MINUTES=30`). It moves forward on *every* authenticated
 *    request, including the ones this client issues on the user's behalf.
 *  - `absolute_expires_at` — a **hard ceiling** fixed when the session was issued
 *    (server default 720 minutes / 12h). Activity does not extend it. Nothing
 *    extends it.
 *
 * The consequence is not an edge case: **a long-lived tab will be signed out
 * mid-session**, while the person is still typing, and that is normal operation
 * rather than an error. This module exists so the UI can see it coming and say so
 * before work is lost, instead of discovering it as a 401 on the save.
 *
 * ## Why there is no keepalive here
 *
 * `GET /auth/session` is an authenticated request, so calling it slides the idle
 * deadline exactly like any other. Polling it on a timer therefore keeps a session
 * alive indefinitely up to the absolute ceiling, which quietly defeats REQ-701's
 * idle expiry — the client would have removed a security property the API paid for.
 * So this module tracks the clocks locally from what login already told us, and
 * issues no requests of its own.
 */

/** The subset of `Session` this module needs. Both fields are RFC 3339 strings. */
export interface SessionExpiry {
  readonly idle_expires_at: string;
  readonly absolute_expires_at: string;
}

/** How close to the absolute ceiling counts as "warn the person now". */
export const DEFAULT_ABSOLUTE_EXPIRY_WARNING_MS = 5 * 60 * 1000;

/** Which clock is the one about to end this session. */
export type ExpiryClock = "idle" | "absolute";

export type SessionLifetimeState =
  /** Both clocks have room. */
  | "live"
  /** The absolute ceiling is close. Activity will not push it back; say so. */
  | "absolute_expiry_imminent"
  /** One of the clocks has passed. The credential is dead; the server will 401. */
  | "expired";

export interface SessionLifetime {
  readonly idleExpiresAt: Date;
  /** Fixed at issue. This value never moves. */
  readonly absoluteExpiresAt: Date;
  /** Whichever comes first — the moment the session actually ends. */
  readonly effectiveExpiresAt: Date;
  /** Which clock `effectiveExpiresAt` came from. */
  readonly expiresBecause: ExpiryClock;
  readonly msUntilIdleExpiry: number;
  readonly msUntilAbsoluteExpiry: number;
  readonly state: SessionLifetimeState;
  /**
   * True when the absolute ceiling is inside the warning window. Kept separate
   * from `state` because it stays true once the session has expired, and because
   * an imminent absolute expiry is the one a person cannot avoid by carrying on
   * working — it is the one worth interrupting them about.
   */
  readonly absoluteExpiryImminent: boolean;
  /** Which clock ended it, or null while it is alive. */
  readonly expiredBecause: ExpiryClock | null;
}

export interface SessionLifetimeOptions {
  readonly now?: Date;
  readonly absoluteExpiryWarningMs?: number;
}

function parseInstant(value: string, field: string): Date {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    throw new TypeError(`Session field ${field} was not a parseable timestamp: ${JSON.stringify(value)}`);
  }
  return parsed;
}

/**
 * Describe both clocks at a moment in time.
 *
 * Pure: it reads the two timestamps it is given and reports. It never issues a
 * request, and in particular never refreshes the session in order to answer.
 */
export function describeSessionLifetime(
  expiry: SessionExpiry,
  options: SessionLifetimeOptions = {},
): SessionLifetime {
  const now = options.now ?? new Date();
  const warningMs = options.absoluteExpiryWarningMs ?? DEFAULT_ABSOLUTE_EXPIRY_WARNING_MS;

  const idleExpiresAt = parseInstant(expiry.idle_expires_at, "idle_expires_at");
  const absoluteExpiresAt = parseInstant(expiry.absolute_expires_at, "absolute_expires_at");

  const msUntilIdleExpiry = idleExpiresAt.getTime() - now.getTime();
  const msUntilAbsoluteExpiry = absoluteExpiresAt.getTime() - now.getTime();

  const idleIsFirst = idleExpiresAt.getTime() <= absoluteExpiresAt.getTime();
  const effectiveExpiresAt = idleIsFirst ? idleExpiresAt : absoluteExpiresAt;
  const expiresBecause: ExpiryClock = idleIsFirst ? "idle" : "absolute";

  const idleExpired = msUntilIdleExpiry <= 0;
  const absoluteExpired = msUntilAbsoluteExpiry <= 0;
  const expired = idleExpired || absoluteExpired;

  // If both have passed, name the absolute one: it is the one no amount of
  // activity could have prevented, so it is the honest explanation.
  const expiredBecause: ExpiryClock | null = absoluteExpired ? "absolute" : idleExpired ? "idle" : null;

  const absoluteExpiryImminent = msUntilAbsoluteExpiry <= warningMs;

  const state: SessionLifetimeState = expired
    ? "expired"
    : absoluteExpiryImminent
      ? "absolute_expiry_imminent"
      : "live";

  return {
    idleExpiresAt,
    absoluteExpiresAt,
    effectiveExpiresAt,
    expiresBecause,
    msUntilIdleExpiry,
    msUntilAbsoluteExpiry,
    state,
    absoluteExpiryImminent,
    expiredBecause,
  };
}

/**
 * A local mirror of the server's two clocks.
 *
 * Login hands us both deadlines. The absolute one never moves again, so we simply
 * keep it. The idle one slides by a fixed window on every authenticated request,
 * so we measure that window once — as `idle_expires_at - issuedAt`, taken from the
 * server rather than hardcoded from `SESSION_IDLE_MINUTES` — and slide it ourselves
 * whenever this client issues a request.
 *
 * This is an estimate of server state, held so the UI can warn without polling. It
 * is never authority: the server decides, and a 401 is the answer that counts.
 */
export interface SessionClock {
  /** Fixed at issue. Nothing in this interface can move it. */
  readonly absoluteExpiresAt: Date;
  /** The idle window's length, observed at login. */
  readonly idleWindowMs: number;
  /** The current idle deadline. */
  idleExpiresAt(): Date;
  /** Record that an authenticated request went out; slides the idle clock only. */
  noteAuthenticatedRequest(at?: Date): void;
  /** Both clocks, right now. */
  describe(options?: SessionLifetimeOptions): SessionLifetime;
}

export function createSessionClock(expiry: SessionExpiry, issuedAt: Date = new Date()): SessionClock {
  const absoluteExpiresAt = parseInstant(expiry.absolute_expires_at, "absolute_expires_at");
  const initialIdleExpiry = parseInstant(expiry.idle_expires_at, "idle_expires_at");

  const idleWindowMs = Math.max(0, initialIdleExpiry.getTime() - issuedAt.getTime());
  let idleDeadline = initialIdleExpiry;

  return {
    absoluteExpiresAt,
    idleWindowMs,

    idleExpiresAt() {
      return idleDeadline;
    },

    noteAuthenticatedRequest(at: Date = new Date()) {
      // The idle window slides. The ceiling does not, and there is deliberately
      // no branch here that touches `absoluteExpiresAt`.
      idleDeadline = new Date(at.getTime() + idleWindowMs);
    },

    describe(options: SessionLifetimeOptions = {}) {
      return describeSessionLifetime(
        {
          idle_expires_at: idleDeadline.toISOString(),
          absolute_expires_at: absoluteExpiresAt.toISOString(),
        },
        options,
      );
    },
  };
}
